/**
 * Keyboard utilities for handling keyboard events
 * This file should remain dependency-free to avoid circular dependencies
 */

import { ValidationInputI } from '@msc-b2c/interfaces/types';

export const keyCodes = {
  ENTER: 'Enter',
  SPACE: ' ',
  END: 'End',
  HOME: 'Home',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_UP: 'ArrowUp',
  ARROW_RIGHT: 'ArrowRight',
  ARROW_DOWN: 'ArrowDown',
  TAB: 'Tab',
  ESCAPE: 'Escape',
};

/**
 * Handle keyboard events for accessibility (Enter/Space)
 * @param event - React keyboard event
 * @param action - Function to execute when Enter or Space is pressed
 * @param args - Optional arguments to pass to the action function
 * @param condition - Optional condition that must be true for the action to execute
 */
export const handleKeyDown = (
  event: React.KeyboardEvent,
  action: (...args: any[]) => void,
  args: any[] = [],
  condition = true
) => {
  if (condition && (event.key === keyCodes.ENTER || event.key === keyCodes.SPACE)) {
    event.preventDefault();
    action(...args);
  }
};

export const SUBMIT_BUTTON_PRESS_FEEDBACK_CLASS = '-press-feedback';

/**
 * Handle Enter on submit buttons. Native Enter activation is unreliable for submit
 * buttons in forms (Space works via the browser default). Mirrors Space: show
 * pressed styles on keydown, activate on keyup so keyboard users get the same
 * visual feedback as a mouse click.
 */
export const handleSubmitButtonKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
  const button = event.currentTarget;

  if (event.key !== keyCodes.ENTER || button.disabled || event.repeat) {
    return;
  }

  event.preventDefault();
  button.classList.add(SUBMIT_BUTTON_PRESS_FEEDBACK_CLASS);
};

export const handleSubmitButtonKeyUp = (event: React.KeyboardEvent<HTMLButtonElement>) => {
  const button = event.currentTarget;

  if (event.key !== keyCodes.ENTER || button.disabled) {
    return;
  }

  if (!button.classList.contains(SUBMIT_BUTTON_PRESS_FEEDBACK_CLASS)) {
    return;
  }

  event.preventDefault();
  button.classList.remove(SUBMIT_BUTTON_PRESS_FEEDBACK_CLASS);
  button.click();
};

export const handleSubmitButtonBlur = (event: React.FocusEvent<HTMLButtonElement>) => {
  event.currentTarget.classList.remove(SUBMIT_BUTTON_PRESS_FEEDBACK_CLASS);
};

/**
 * Submit a form when Enter is pressed on a text control or submit button.
 */
export const handleFormEnterSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
  if (event.key !== keyCodes.ENTER || event.defaultPrevented) {
    return;
  }

  const target = event.target as HTMLElement;
  const isTextControl =
    target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
  const isSubmitButton =
    target.tagName === 'BUTTON' && (target as HTMLButtonElement).type === 'submit';

  if (!isTextControl && !isSubmitButton) {
    return;
  }

  event.preventDefault();
  event.currentTarget.requestSubmit();
};

export type HandleTabListKeyDownOptions = {
  tabCount: number;
  currentIndex: number;
  onSelectTab: (index: number) => void;
  focusTab?: (index: number) => void;
};

/**
 * Keyboard handler for WAI-ARIA tablist controls.
 * Supports arrow keys (with wrap), Home/End, Enter, and Space.
 */
export const handleTabListKeyDown = (
  event: React.KeyboardEvent,
  { tabCount, currentIndex, onSelectTab, focusTab }: HandleTabListKeyDownOptions
) => {
  if (tabCount <= 0) return;

  const lastIndex = tabCount - 1;
  let newIndex: number | null = null;

  switch (event.key) {
    case keyCodes.ARROW_LEFT:
      newIndex = currentIndex > 0 ? currentIndex - 1 : lastIndex;
      break;
    case keyCodes.ARROW_RIGHT:
      newIndex = currentIndex < lastIndex ? currentIndex + 1 : 0;
      break;
    case keyCodes.HOME:
      newIndex = 0;
      break;
    case keyCodes.END:
      newIndex = lastIndex;
      break;
    case keyCodes.ENTER:
    case keyCodes.SPACE:
      event.preventDefault();
      onSelectTab(currentIndex);
      return;
    default:
      return;
  }

  if (newIndex !== null) {
    event.preventDefault();
    onSelectTab(newIndex);
    focusTab?.(newIndex);
  }
};

/**
 * Get the keyboard type based on validation rules
 * @param validationRules - Array of validation rules for the input field
 * @returns "text" | "numeric" - The appropriate keyboard type for the input field
 */
export const getKeyboardType = (
  validationRules: ValidationInputI[] | undefined
): 'text' | 'numeric' => {
  if (!validationRules || validationRules.length === 0) return 'text';

  const numericValidation = new Set([
    'vr-only-numeric-min-3-digit',
    'vr-min-6-digit-and-max-13-digit',
  ]);
  const isNumeric = validationRules?.some((validation) =>
    numericValidation.has(validation.validationRules)
  );

  if (isNumeric) {
    return 'numeric';
  }

  return 'text';
};
